import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'age', 'sex', 'method',
    // Cooper test inputs
    'cooperInputs', 'cooperDistance',
    // Rockport test inputs
    'rockportInputs', 'weight', 'weightUnit', 'walkMinutes', 'walkSeconds', 'walkHr',
    // Heart rate method inputs
    'hrInputs', 'restingHr', 'maxHr',
    // Results
    'vo2maxDisplay', 'fitnessLevel', 'fitnessDescription', 'fitnessLevelCard',
    'percentile', 'percentileText', 'percentileBar',
    'ageGroup', 'sexDisplay',
    'rangePoor', 'rangeFair', 'rangeGood', 'rangeExcellent', 'rangeSuperior',
    'methodNote'
  ]

  // ACSM VO2 Max classification tables (mL/kg/min)
  maleClassifications = {
    '20-29': { poor: [0, 37], fair: [38, 41], good: [42, 45], excellent: [46, 52], superior: [53, 100]},
    '30-39': { poor: [0, 35], fair: [36, 39], good: [40, 43], excellent: [44, 50], superior: [51, 100]},
    '40-49': { poor: [0, 33], fair: [34, 37], good: [38, 41], excellent: [42, 48], superior: [49, 100]},
    '50-59': { poor: [0, 30], fair: [31, 34], good: [35, 38], excellent: [39, 44], superior: [45, 100]},
    '60-69': { poor: [0, 26], fair: [27, 30], good: [31, 35], excellent: [36, 41], superior: [42, 100]},
    '70+': { poor: [0, 22], fair: [23, 26], good: [27, 31], excellent: [32, 37], superior: [38, 100]}
  }

  femaleClassifications = {
    '20-29': { poor: [0, 30], fair: [31, 34], good: [35, 38], excellent: [39, 44], superior: [45, 100]},
    '30-39': { poor: [0, 28], fair: [29, 32], good: [33, 36], excellent: [37, 42], superior: [43, 100]},
    '40-49': { poor: [0, 25], fair: [26, 29], good: [30, 33], excellent: [34, 39], superior: [40, 100]},
    '50-59': { poor: [0, 22], fair: [23, 26], good: [27, 31], excellent: [32, 37], superior: [38, 100]},
    '60-69': { poor: [0, 19], fair: [20, 23], good: [24, 27], excellent: [28, 33], superior: [34, 100]},
    '70+': { poor: [0, 17], fair: [18, 21], good: [22, 25], excellent: [26, 31], superior: [32, 100]}
  }

  fitnessDescriptions = {
    poor: 'Below average cardio fitness. Regular aerobic exercise recommended.',
    fair: 'Average cardio fitness. Continue building aerobic base.',
    good: 'Above average cardio fitness. Good foundation for training.',
    excellent: 'Well above average. Strong cardiovascular system.',
    superior: 'Elite cardio fitness. Top-tier endurance capacity.'
  }

  fitnessPercentiles = {
    poor: 15,
    fair: 35,
    good: 55,
    excellent: 75,
    superior: 92
  }

  connect() {
    this.updateMethodInputs()
    this.calculate()
  }

  updateMethodInputs() {
    const method = this.methodTargets.find((r) => r.checked)?.value || 'cooper'

    // Hide all method-specific inputs
    this.cooperInputsTarget.classList.add('hidden')
    this.rockportInputsTarget.classList.add('hidden')
    this.hrInputsTarget.classList.add('hidden')

    // Show the relevant inputs
    if (method === 'cooper') {
      this.cooperInputsTarget.classList.remove('hidden')
    } else if (method === 'rockport') {
      this.rockportInputsTarget.classList.remove('hidden')
    } else if (method === 'heartrate') {
      this.hrInputsTarget.classList.remove('hidden')
    }

    this.calculate()
  }

  calculate() {
    const age = parseInt(this.ageTarget.value, 10) || 35
    const sex = this.sexTargets.find((r) => r.checked)?.value || 'male'
    const method = this.methodTargets.find((r) => r.checked)?.value || 'cooper'

    let vo2max = 0
    let methodNote = ''

    if (method === 'cooper') {
      vo2max = this.calculateCooper()
      methodNote = 'Cooper 12-minute run test formula'
    } else if (method === 'rockport') {
      vo2max = this.calculateRockport(age, sex)
      methodNote = 'Rockport 1-mile walk test formula'
    } else if (method === 'heartrate') {
      vo2max = this.calculateHeartRate(age)
      methodNote = 'Heart rate ratio estimation (Uth et al.)'
    }

    // Display VO2 Max
    this.vo2maxDisplayTarget.textContent = vo2max.toFixed(1)
    this.methodNoteTarget.textContent = methodNote

    // Get classification
    const ageGroup = this.getAgeGroup(age)
    const classifications = sex === 'male' ? this.maleClassifications : this.femaleClassifications
    const ranges = classifications[ageGroup]
    const fitnessLevel = this.getFitnessLevel(vo2max, ranges)
    const percentile = this.fitnessPercentiles[fitnessLevel]

    // Update fitness level display
    this.fitnessLevelTarget.textContent = fitnessLevel.toUpperCase()
    this.fitnessDescriptionTarget.textContent = this.fitnessDescriptions[fitnessLevel]

    // Update fitness level card styling
    this.updateFitnessLevelStyling(fitnessLevel)

    // Update percentile
    this.percentileTarget.textContent = percentile
    this.percentileTextTarget.textContent = percentile
    this.percentileBarTarget.style.width = `${percentile}%`

    // Update age group and sex display
    this.ageGroupTarget.textContent = ageGroup
    this.sexDisplayTarget.textContent = sex === 'male' ? 'Male' : 'Female'

    // Update range displays
    this.rangePoorTarget.textContent = `<${ranges.poor[1] + 1}`
    this.rangeFairTarget.textContent = `${ranges.fair[0]}-${ranges.fair[1]}`
    this.rangeGoodTarget.textContent = `${ranges.good[0]}-${ranges.good[1]}`
    this.rangeExcellentTarget.textContent = `${ranges.excellent[0]}-${ranges.excellent[1]}`
    this.rangeSuperiorTarget.textContent = `${ranges.superior[0]}+`
  }

  calculateCooper() {
    // Cooper formula: VO2max = (distance_meters - 504.9) / 44.73
    const distance = parseFloat(this.cooperDistanceTarget.value) || 2400
    return (distance - 504.9) / 44.73
  }

  calculateRockport(age, sex) {
    // Rockport formula:
    // VO2max = 132.853 - (0.0769 x weight_lbs) - (0.3877 x age) + (6.315 x gender) - (3.2649 x time_min) - (0.1565 x HR)
    // Gender: male = 1, female = 0
    let weight = parseFloat(this.weightTarget.value) || 70
    const weightUnit = this.weightUnitTarget.value
    const minutes = parseInt(this.walkMinutesTarget.value, 10) || 15
    const seconds = parseInt(this.walkSecondsTarget.value, 10) || 0
    const hr = parseInt(this.walkHrTarget.value, 10) || 130

    // Convert to lbs if needed
    if (weightUnit === 'kg') {
      weight *= 2.20462
    }

    const timeMinutes = minutes + (seconds / 60)
    const genderValue = sex === 'male' ? 1 : 0

    const vo2max = 132.853 - (0.0769 * weight) - (0.3877 * age) + (6.315 * genderValue) - (3.2649 * timeMinutes) - (0.1565 * hr)

    return Math.max(vo2max, 10) // Ensure reasonable minimum
  }

  calculateHeartRate(age) {
    // Heart rate ratio method (Uth et al.):
    // VO2max = 15.3 x (maxHR / restingHR)
    const restingHr = parseInt(this.restingHrTarget.value, 10) || 60
    const inputMaxHr = parseInt(this.maxHrTarget.value, 10)

    // Estimate max HR using Tanaka formula if not provided
    const maxHr = inputMaxHr || Math.round(208 - (0.7 * age))

    return 15.3 * (maxHr / restingHr)
  }

  getAgeGroup(age) {
    if (age < 20) return '20-29'
    if (age < 30) return '20-29'
    if (age < 40) return '30-39'
    if (age < 50) return '40-49'
    if (age < 60) return '50-59'
    if (age < 70) return '60-69'
    return '70+'
  }

  getFitnessLevel(vo2max, ranges) {
    if (vo2max >= ranges.superior[0]) return 'superior'
    if (vo2max >= ranges.excellent[0]) return 'excellent'
    if (vo2max >= ranges.good[0]) return 'good'
    if (vo2max >= ranges.fair[0]) return 'fair'
    return 'poor'
  }

  updateFitnessLevelStyling(level) {
    const _card = this.fitnessLevelCardTarget
    const badge = this.fitnessLevelTarget

    // Reset classes
    badge.className = 'px-3 py-1 text-sm font-medium'

    // Apply level-specific styling
    const styles = {
      poor: 'bg-red-600 text-white',
      fair: 'bg-orange-500 text-white',
      good: 'bg-yellow-500 text-gray-900',
      excellent: 'bg-green-600 text-white',
      superior: 'bg-blue-600 text-white'
    }

    badge.classList.add(...(styles[level] || styles.good).split(' '))
  }
}
